import { FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { LanguageToggle } from "@/components/ui/LanguageToggle";
import { useTranslation } from "@/i18n/useTranslation";

interface AddScriptFormProps {
  values: {
    movieName: string;
    customMessage: string;
    scriptUrl: string;
    scriptUrlTh: string;
    passwordProtected: boolean;
  };
  onChangeValues: (changes: Partial<AddScriptFormProps["values"]>) => void;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const AddScriptForm = ({
  values,
  onChangeValues,
  onCancel,
  onSubmit,
  isSubmitting,
}: AddScriptFormProps) => {
  const { t } = useTranslation();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <>
      <DialogHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:pr-12">
          <div>
            <DialogTitle>{t("add_script_form.title")}</DialogTitle>
            <DialogDescription>
              {t("add_script_form.description")}
            </DialogDescription>
          </div>
          <LanguageToggle className="sm:self-start" />
        </div>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="movieName">{t("add_script_form.movie_name_label")}</Label>
          <Input
            id="movieName"
            value={values.movieName}
            onChange={(event) => onChangeValues({ movieName: event.target.value })}
            placeholder={t("add_script_form.movie_name_placeholder")}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="customMessage">{t("add_script_form.custom_message_label")}</Label>
          <Textarea
            id="customMessage"
            value={values.customMessage}
            onChange={(event) => onChangeValues({ customMessage: event.target.value })}
            placeholder={t("add_script_form.custom_message_placeholder")}
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="scriptUrl">{t("add_script_form.script_url_label")}</Label>
          <Input
            id="scriptUrl"
            value={values.scriptUrl}
            onChange={(event) => onChangeValues({ scriptUrl: event.target.value })}
            placeholder={t("add_script_form.script_url_placeholder")}
            type="url"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="scriptUrlTh">{t("add_script_form.script_url_th_label")}</Label>
          <Input
            id="scriptUrlTh"
            value={values.scriptUrlTh}
            onChange={(event) => onChangeValues({ scriptUrlTh: event.target.value })}
            placeholder={t("add_script_form.script_url_th_placeholder")}
            type="url"
          />
        </div>

        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-1">
            <Label htmlFor="passwordProtected">{t("add_script_form.password_label")}</Label>
            <p className="text-xs text-muted-foreground max-w-[28rem]">
              {t("add_script_form.password_help")}
            </p>
          </div>
          <Switch
            id="passwordProtected"
            checked={values.passwordProtected}
            onCheckedChange={(checked) => onChangeValues({ passwordProtected: checked })}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("add_script_form.submit_button")}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};
